/**
 * 对话工作流风格库：与 ConversationWorkflow 使用相同接口与归一化规则
 * - GET /normal/categories
 * - POST /search  { page } 或 { category_id, page }
 */

import api from '@/utils/api'

/**
 * @param {object} it
 * @param {number} idx
 * @param {string} categoryId
 */
export function normalizeWorkflowStyleItem(it, idx, categoryId) {
  const cid = categoryId != null ? String(categoryId) : 'all'
  const o = it && typeof it === 'object' ? it : {}
  const id = o.id != null ? String(o.id) : `${cid}-${Date.now()}-${idx}`
  const name = String(o.name || o.title || o.label || '未命名风格')
  const thumb = String(o.thumb || o.cover || o.image || o.url || '')
  const desc = String(o.desc || o.description || o.remark || '')
  const prompt = String(o.prompt || o.style_prompt || o.stylePrompt || '')
  const refDataUrl = String(o.refDataUrl || o.ref_data_url || '')
  const refUrl = String(o.refUrl || o.ref_url || '')
  const badge = o.badge != null ? String(o.badge) : ''
  return { id, tab: cid, name, thumb, desc, prompt, badge, refDataUrl, refUrl }
}

function pick(obj, keys) {
  if (!obj || typeof obj !== 'object') return null
  for (const k of keys) {
    if (obj[k] != null) return obj[k]
  }
  return null
}

/**
 * 合并一次 POST /search 的返回（分页、是否结束），与 ConversationWorkflow.fetchCategoryStyles 对齐
 * @param {any} res axios 响应体（含 res.data）
 * @param {{ categoryId: string, reset: boolean, previousBucket: { page?: number, list?: any[], finished?: boolean, finishedReason?: string } | null }} opts
 * @returns {{ page: number, list: object[], finished: boolean, finishedReason: string }}
 */
export function mergeWorkflowStyleSearchResponse(res, opts) {
  const categoryId = opts && opts.categoryId != null ? String(opts.categoryId) : ''
  const reset = !!(opts && opts.reset)
  const cur = (opts && opts.previousBucket) || null
  const cid = categoryId || 'all'

  const data = res && res.data ? res.data : res
  const list =
    (data && Array.isArray(data.list) && data.list) ||
    (res && res.data && Array.isArray(res.data.list) && res.data.list) ||
    (res && Array.isArray(res.list) && res.list) ||
    []

  const normalized = list.map((it, idx) => normalizeWorkflowStyleItem(it, idx, cid))
  const prev = reset ? [] : cur && Array.isArray(cur.list) ? cur.list : []
  const merged = prev.concat(normalized)
  const nextPage = reset ? 1 : Math.max(1, parseInt(cur && cur.page, 10) || 1)

  let finished = normalized.length === 0
  let finishedReason = finished ? 'empty_list' : ''

  try {
    const pageMeta =
      (data && (data.pagination || data.paging || data.page_meta || data.pageMeta || data.meta)) || null

    const hasMore = pick(data, ['has_more', 'hasMore']) ?? pick(pageMeta, ['has_more', 'hasMore'])
    if (hasMore === false || hasMore === 0 || hasMore === '0') {
      finished = true
      finishedReason = 'has_more=false'
    }
    const currentPage =
      pick(data, ['current_page', 'currentPage', 'page']) ?? pick(pageMeta, ['current_page', 'currentPage', 'page'])
    const lastPage =
      pick(data, ['last_page', 'lastPage', 'pages', 'total_pages', 'totalPages']) ??
      pick(pageMeta, ['last_page', 'lastPage', 'pages', 'total_pages', 'totalPages'])
    if (!finished && currentPage != null && lastPage != null && Number(currentPage) >= Number(lastPage)) {
      finished = true
      finishedReason = 'current>=last'
    }
    const nextUrl =
      pick(data, ['next_page_url', 'nextPageUrl']) ?? pick(pageMeta, ['next_page_url', 'nextPageUrl'])
    if (nextUrl === null) {
      if (
        (data &&
          (Object.prototype.hasOwnProperty.call(data, 'next_page_url') ||
            Object.prototype.hasOwnProperty.call(data, 'nextPageUrl'))) ||
        (pageMeta &&
          (Object.prototype.hasOwnProperty.call(pageMeta, 'next_page_url') ||
            Object.prototype.hasOwnProperty.call(pageMeta, 'nextPageUrl')))
      ) {
        finished = true
        finishedReason = 'next_url=null'
      }
    }

    const perPage =
      pick(data, ['per_page', 'perPage', 'page_size', 'pageSize', 'limit', 'page_limit', 'pageLimit']) ??
      pick(pageMeta, ['per_page', 'perPage', 'page_size', 'pageSize', 'limit', 'page_limit', 'pageLimit'])
    if (!finished && perPage != null && Number(perPage) > 0) {
      if (normalized.length < Number(perPage)) {
        finished = true
        finishedReason = 'len<per_page'
      }
    }

    const total =
      pick(data, ['total', 'total_count', 'totalCount', 'count']) ??
      pick(pageMeta, ['total', 'total_count', 'totalCount', 'count'])
    if (!finished && total != null && Number(total) >= 0) {
      if (merged.length >= Number(total)) {
        finished = true
        finishedReason = 'merged>=total'
      }
    }
  } catch {
    // ignore
  }

  return {
    page: nextPage + 1,
    list: merged,
    finished,
    finishedReason
  }
}

export async function fetchWorkflowStyleCategoriesList() {
  const res = await api.get('/normal/categories')
  const list =
    (res && res.data && Array.isArray(res.data.list) && res.data.list) ||
    (res && Array.isArray(res.list) && res.list) ||
    []
  return Array.isArray(list) ? list : []
}

export async function postWorkflowStyleSearch({ categoryId, page }) {
  const cid = categoryId != null ? String(categoryId) : 'all'
  const p = Math.max(1, Number(page) || 1)
  return api.post('/search', cid === 'all' ? { page: p } : { category_id: cid, page: p })
}

/**
 * 与 ConversationWorkflow 中 Seedream 出图一致：风格参考 URL（dataURL / http）
 * @param {object|null|undefined} style
 * @returns {string}
 */
export function pickWorkflowStyleRef(style) {
  if (!style || typeof style !== 'object') return ''
  return (
    (style.refDataUrl ? String(style.refDataUrl) : '').trim() ||
    (style.refUrl ? String(style.refUrl) : '').trim() ||
    (style.thumb ? String(style.thumb) : '').trim()
  )
}

/** 节点是否已配置可用风格（本节点 workflowStyle） */
export function isWorkflowStyleSet(style) {
  if (!style || typeof style !== 'object') return false
  return !!(
    (style.id != null && String(style.id).trim()) ||
    (style.name != null && String(style.name).trim()) ||
    (style.prompt != null && String(style.prompt).trim()) ||
    pickWorkflowStyleRef(style)
  )
}

/** @param {object|null|undefined} style */
export function cloneWorkflowStylePlain(style) {
  if (!isWorkflowStyleSet(style)) return null
  try {
    return JSON.parse(JSON.stringify(style))
  } catch {
    return { ...style }
  }
}

/** @param {object|null|undefined} sbNode */
export function workflowStyleFromStoryboardNode(sbNode) {
  if (
    !sbNode ||
    !sbNode.data ||
    (sbNode.type !== 'storyboard' && sbNode.type !== 'storyPanel')
  ) {
    return null
  }
  return cloneWorkflowStylePlain(sbNode.data.workflowStyle)
}

/**
 * @param {string} basePrompt 用户提示词（已含上游文本等）
 * @param {object|null|undefined} style 归一化风格对象
 * @returns {string}
 */
export function buildWorkflowStylePromptSuffix(style) {
  if (!style || typeof style !== 'object') return ''
  const styleName = style.name != null ? String(style.name) : ''
  const stylePrompt = style.prompt != null ? String(style.prompt).trim() : ''
  const styleRef = pickWorkflowStyleRef(style)
  if (!styleName && !stylePrompt && !styleRef) return ''
  return `\n\nSTYLE REQUIREMENTS:\n- Apply the selected style strictly (${styleName || 'custom style'}).\n${
    stylePrompt ? `- Style keywords: ${stylePrompt}\n` : ''
  }- The provided reference image is a STYLE REFERENCE (art style/materials/lighting/color palette), not a subject reference.\n- Do NOT copy the reference subject/identity.\n- Clean layout, white or light gray background.\n`
}
